import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChefHat, Wine, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import FixedFooter from '@/components/common/FixedFooter';

const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      try {
        // Search in both outlets
        const [cafePlusResponse, plusOneResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu/items?outlet=cafe-plus`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu/items?outlet=plus-one`)
        ]);
        
        const cafePlusItems = await cafePlusResponse.json();
        const plusOneItems = await plusOneResponse.json();
        
        const query = searchQuery.toLowerCase();
        
        // Check if item exists in Café Plus
        const foundInCafePlus = cafePlusItems.some(item => 
          item.name.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query) ||
          item.category.toLowerCase().includes(query)
        );
        
        // Check if item exists in Plus One
        const foundInPlusOne = plusOneItems.some(item => 
          item.name.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query) ||
          item.category.toLowerCase().includes(query)
        );
        
        // Navigate to appropriate page
        if (foundInCafePlus) {
          navigate(`/cafe-plus?search=${encodeURIComponent(searchQuery)}`);
        } else if (foundInPlusOne) {
          navigate(`/plus-one?search=${encodeURIComponent(searchQuery)}`);
        } else {
          // Default to Café Plus if not found in either
          navigate(`/cafe-plus?search=${encodeURIComponent(searchQuery)}`);
        }
      } catch (error) {
        // Fallback to Café Plus on error
        navigate(`/cafe-plus?search=${encodeURIComponent(searchQuery)}`);
      }
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: 'url(/images/bwp-bg.jpg)',
          filter: 'brightness(1.1) contrast(1.05) saturate(1.1)'
        }}
      />
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-white bg-opacity-60" />


      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="pt-4 px-4 md:px-8">
          <div className="max-w-7xl mx-auto flex justify-between items-start">
            <img 
              src="/images/logo-mobile.webp"
              alt="Best Western Plus"
              className="h-12 md:h-16"
            />
            <button
              onClick={() => navigate('/events')}
              className="btn-secondary text-sm px-3 py-2 mt-2"
            >
              Events
            </button>
          </div>
        </header>

        {/* Hero Section */}
        <main className="flex-1 flex items-center justify-center px-4 py-6">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Title */}
            <div className="space-y-4">
              <h1 
                className="text-3xl sm:text-4xl lg:text-5xl leading-tight font-bold"
                style={{ color: 'var(--burgundy)' }}
                data-testid="main-title"
              >
                ENTER THE AMAZING<br />DINING & BAR EXPERIENCE
              </h1>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="glass rounded-full p-4 shadow-xl">
                <Input
                  type="text"
                  placeholder="Search for dishes, beverages, or spirits..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-transparent border-none text-base focus:outline-none focus:ring-0 text-center"
                  data-testid="home-search-input"
                  style={{ color: 'var(--charcoal)' }}
                />
              </div>
            </form>

            {/* Outlet Cards */}
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              {/* Café Plus Card */}
              <div
                onClick={() => navigate('/cafe-plus')}
                className="glass-burgundy rounded-3xl p-8 cursor-pointer group hover:shadow-2xl"
                data-testid="cafe-plus-card"
              >
                <div className="flex flex-col items-center space-y-6">
                  <img
                    src="/images/2 25.png"
                    alt="Café Plus"
                    className="h-32 w-auto object-contain"
                  />
                  <div className="text-center space-y-3">
                    <h2 className="text-2xl" style={{ color: 'var(--burgundy)' }}>
                      All-Day Dining
                    </h2>
                    <p className="text-sm" style={{ color: 'var(--charcoal)' }}>
                      Breakfast, Lunch & Dinner with a diverse menu
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-burgundy group-hover:translate-x-2" style={{ color: 'var(--burgundy)' }}>
                    <ChefHat size={20} />
                    <span className="font-medium">Explore Menu</span>
                  </div>
                </div>
              </div>

              {/* Plus One Card */}
              <div
                onClick={() => navigate('/plus-one')}
                className="glass-burgundy rounded-3xl p-8 cursor-pointer group hover:shadow-2xl"
                data-testid="plus-one-card"
              >
                <div className="flex flex-col items-center space-y-6">
                  <img
                    src="/images/1 19.png"
                    alt="Plus One"
                    className="h-32 w-auto object-contain"
                  />
                  <div className="text-center space-y-3">
                    <h2 className="text-2xl" style={{ color: 'var(--burgundy)' }}>
                      The Cocktail Bar
                    </h2>
                    <p className="text-sm" style={{ color: 'var(--charcoal)' }}>
                      Premium cocktails, spirits & beverages
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-burgundy group-hover:translate-x-2" style={{ color: 'var(--burgundy)' }}>
                    <Wine size={20} />
                    <span className="font-medium">Explore Menu</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-8 text-center text-sm" style={{ color: 'var(--charcoal)' }}>
          <p>© 2025 Best Western Plus Hotel Tejvivaan. All rights reserved.</p>
          {/* Powered by Absolin */}
          <FixedFooter />
        </footer>
      </div>
    </div>
  );
};

export default HomePage;
